// CourseContentView.js - Full-page course content browser with integrated tutoring
// Shows all slides/topics and allows users to learn any topic with tutor guidance

import React, { useState, useEffect, useRef } from 'react';
import {API_BASE_URL, apiFetch, isDemoMode } from '../config';
import { renderMessageContent } from '../utils';
import { analyzeStruggleInBackground } from '../utils/struggleDetection';
import { getCourseContent, saveCourseContent } from '../services/CourseContentService';
import { getCurriculumUrl, getSlidesProacticesUrl } from '../App';
import MessageContent from './MessageContent';
import CodeRunnerModal from './CodeRunnerModal';

// Animated "Thinking" component with growing dots
const ThinkingIndicator = () => {
  const [dots, setDots] = useState('');
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev.length >= 5) return '';
        return prev + '.';
      });
    }, 400);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <span style={{ 
      fontSize: '18px', 
      fontWeight: '500',
      color: '#58a6ff',
      letterSpacing: '1px'
    }}>
      🤔 Thinking{dots}
    </span>
  );
};

// Helper: Wrap "Code:" sections in markdown code fences for proper display
// Detects "Code:" followed by indented code and wraps it in ```python ... ```
const wrapCodeSections = (content) => {
  if (!content) return content;
  
  const lines = content.split('\n');
  const result = [];
  let inCodeSection = false;
  let codeLines = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Detect start of code section (line is just "Code:" or "Code: " possibly with trailing whitespace)
    if (line.trim().toLowerCase() === 'code:' || line.trim().toLowerCase() === 'code') {
      // Start collecting code
      inCodeSection = true;
      codeLines = [];
      result.push('**Code:**');
      result.push('```python');
    }
    // If in code section, check if we've hit a non-code line (Description:, empty line after code, etc.)
    else if (inCodeSection) {
      // Check if this line starts a new section or is clearly not code
      const trimmed = line.trim();
      const isNewSection = /^(Description|Output|Note|Example|Sample|Expected|Input|Result|Hint):/i.test(trimmed);
      const isEmptyAfterCode = trimmed === '' && codeLines.length > 0 && 
        (i + 1 >= lines.length || /^(Description|Output|Note|Example|Sample|Expected|Input|Result|Hint|PROBLEM|EXAMPLE|CONCEPT):/i.test(lines[i + 1]?.trim() || ''));
      
      if (isNewSection || (isEmptyAfterCode && i + 1 < lines.length)) {
        // End code section
        result.push(codeLines.join('\n'));
        result.push('```');
        result.push(''); // blank line
        inCodeSection = false;
        codeLines = [];
        result.push(line);
      } else if (trimmed === '' && codeLines.length === 0) {
        // Skip empty lines right after "Code:"
        continue;
      } else {
        // This is code content
        codeLines.push(line);
      }
    }
    else {
      result.push(line);
    }
  }
  
  // Don't forget to close if we ended while in code section
  if (inCodeSection && codeLines.length > 0) {
    result.push(codeLines.join('\n'));
    result.push('```');
  }
  
  return result.join('\n');
};

const CourseContentView = ({ username, onClose, logAction, selectedSection }) => {
  // Dynamic course content loaded from JSON
  const [courseContent, setCourseContent] = useState([]);
  const [courseContentLoading, setCourseContentLoading] = useState(true);
  const [courseContentError, setCourseContentError] = useState(null);

  // Load course structure from JSON file on mount
  useEffect(() => {
    const loadCourseStructure = async () => {
      setCourseContentLoading(true);
      try {
        const response = await fetch(getCurriculumUrl('course_structure.json') + '?t=' + Date.now());
        if (!response.ok) {
          throw new Error('Failed to load course structure');
        }
        const data = await response.json();
        setCourseContent(data);
        setCourseContentError(null);
        console.log('📚 Loaded course structure:', data.length, 'chapters');
      } catch (error) {
        console.error('Error loading course structure:', error);
        setCourseContentError('Failed to load course content. Please check course_structure.json');
        setCourseContent([]);
      } finally {
        setCourseContentLoading(false);
      }
    };
    loadCourseStructure();
  }, [selectedSection]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedSlides, setExpandedSlides] = useState({});
  const [speakingMessageIndex, setSpeakingMessageIndex] = useState(null); // Track which message is being read
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false); // Track when to scroll
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null); // Ref for chat container to scroll
  const currentSpeakingIndexRef = useRef(null); // Track which message should be speaking
  const exerciseDropdownRef = useRef(null); // Ref for click-outside detection
  
  // Exercise mode state
  const [exerciseMode, setExerciseMode] = useState(false); // Are we in exercise mode?
  const [exercises, setExercises] = useState([]); // List of exercises for current slide
  const [selectedExercise, setSelectedExercise] = useState(null); // Currently selected exercise
  const [exerciseDropdownOpen, setExerciseDropdownOpen] = useState(false); // Dropdown visibility
  const [showScratchPad, setShowScratchPad] = useState(false); // Scratch Pad modal
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Sidebar toggle
  const [lastVisitedTopic, setLastVisitedTopic] = useState(null); // Remember last topic for highlighting

  // Attempt tracking for hint progression
  const attemptCountRef = useRef(0); // Current attempt count for current exercise/topic
  
  // Analyze messages to count student attempts (code submissions or wrong answers)
  const countStudentAttempts = (messageHistory) => {
    let attempts = 0;
    
    for (let i = 0; i < messageHistory.length; i++) {
      const msg = messageHistory[i];
      if (msg.role === 'user') {
        const content = msg.content || '';
        // Count as attempt if:
        // 1. Contains code (```python or indented code patterns)
        // 2. Looks like an answer attempt (not just "help", "hint", questions)
        const hasCode = content.includes('```python') || content.includes('def ') || 
                       content.includes('print(') || content.includes('for ') ||
                       content.includes('while ') || content.includes('if ');
        const isQuestion = content.trim().endsWith('?');
        const isHelpRequest = /^(help|hint|i don't know|i'm stuck|what|how|why|can you)/i.test(content.trim());
        
        if (hasCode || (!isQuestion && !isHelpRequest && content.trim().length > 2)) {
          attempts++;
        }
      }
      // Reset attempts if tutor says something indicating new problem/step
      if (msg.role === 'assistant') {
        const content = msg.content || '';
        if (content.includes('Let\'s try a different') || 
            content.includes('Moving on to') ||
            content.includes('Next exercise') ||
            content.includes('new problem')) {
          attempts = 0;
        }
      }
    }
    
    return attempts;
  };

  // Tutor config (loaded from instructor config file)
  const [tutorConfig, setTutorConfig] = useState({
    enabledTutorModes: { stepByStep: true, allSteps: true, adaptive: true },
    defaultMode: 'stepByStep'
  });

  // Progress tracking - which chapters/topics have been studied or skipped
  // Status: undefined/missing = not started, 'studied' = completed, 'skipped' = not covered in class
  const [studyProgress, setStudyProgress] = useState(() => {
    const saved = localStorage.getItem('studyProgress');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Migrate old true/false format to new 'studied'/'skipped' format
      // Remove null/false entries entirely (not started = key doesn't exist)
      const migratedChapters = {};
      const migratedTopics = {};
      
      for (const [key, value] of Object.entries(parsed.chapters || {})) {
        if (value === true || value === 'studied') {
          migratedChapters[key] = 'studied';
        } else if (value === 'skipped') {
          migratedChapters[key] = 'skipped';
        }
        // Don't add false/null entries - absence means "not started"
      }
      
      for (const [key, value] of Object.entries(parsed.topics || {})) {
        if (value === true || value === 'studied') {
          migratedTopics[key] = 'studied';
        } else if (value === 'skipped') {
          migratedTopics[key] = 'skipped';
        }
        // Don't add false/null entries - absence means "not started"
      }
      
      return { chapters: migratedChapters, topics: migratedTopics };
    }
    return {
      chapters: {}, // { chapterId: 'studied' | 'skipped' }
      topics: {}    // { "chapterId-topicIndex": 'studied' | 'skipped' }
    };
  });

  // Helper to normalize status (handles old true/false values)
  const normalizeStatus = (value) => {
    if (value === true || value === 'studied') return 'studied';
    if (value === 'skipped') return 'skipped';
    // false, null, undefined, or anything else = not started
    return null;
  };

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('studyProgress', JSON.stringify(studyProgress));
  }, [studyProgress]);

  // Cycle chapter status: (not set) -> studied -> skipped -> (delete/not set)
  const toggleChapterComplete = (e, chapterId) => {
    e.stopPropagation();
    setStudyProgress(prev => {
      const currentStatus = normalizeStatus(prev.chapters[chapterId]);
      const newChapters = { ...prev.chapters };
      
      if (!currentStatus) {
        newChapters[chapterId] = 'studied';
      } else if (currentStatus === 'studied') {
        newChapters[chapterId] = 'skipped';
      } else {
        // Clear by deleting the key
        delete newChapters[chapterId];
      }
      
      return {
        ...prev,
        chapters: newChapters
      };
    });
  };

  // Cycle topic status: (not set) -> studied -> skipped -> (delete/not set)
  const toggleTopicComplete = (e, chapterId, topicIndex) => {
    e.stopPropagation();
    const key = `${chapterId}-${topicIndex}`;
    setStudyProgress(prev => {
      const currentStatus = normalizeStatus(prev.topics[key]);
      const newTopics = { ...prev.topics };
      
      if (!currentStatus) {
        newTopics[key] = 'studied';
      } else if (currentStatus === 'studied') {
        newTopics[key] = 'skipped';
      } else {
        // Clear by deleting the key
        delete newTopics[key];
      }
      
      return {
        ...prev,
        topics: newTopics
      };
    });
  };

  // Get chapter status for display - completely independent from topics
  const getChapterStatus = (slide) => {
    return normalizeStatus(studyProgress.chapters[slide.id]);
  };

  // Get count of completed/skipped topics in a chapter (just for display)
  const getCompletedTopicsCount = (slide) => {
    if (!slide.topics) return { studied: 0, skipped: 0 };
    const studied = slide.topics.filter((_, idx) => normalizeStatus(studyProgress.topics[`${slide.id}-${idx}`]) === 'studied').length;
    const skipped = slide.topics.filter((_, idx) => normalizeStatus(studyProgress.topics[`${slide.id}-${idx}`]) === 'skipped').length;
    return { studied, skipped };
  };

  // Load tutor config from JSON file
  useEffect(() => {
    const loadTutorConfig = async () => {
      try {
        const response = await fetch('/tutor_config.json?t=' + Date.now());
        if (response.ok) {
          const config = await response.json();
          setTutorConfig(config);
          console.log('🎓 Loaded tutor config:', config);
        }
      } catch (error) {
        console.log('Using default tutor config');
      }
    };
    loadTutorConfig();
  }, []);

  // Settings state (persisted to localStorage)
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('courseContentSettings');
    return saved ? JSON.parse(saved) : {
      speechEnabled: true,
      speechSpeed: 1.0,
      theme: 'dark', // 'dark', 'light', 'sepia', 'highContrast'
      fontSize: 18, // 14, 16, 18, 20
      tutorMode: 'stepByStep' // 'stepByStep', 'allSteps', 'adaptive'
    };
  });

  // Ensure selected tutor mode is enabled, otherwise use default
  useEffect(() => {
    const currentMode = settings.tutorMode || 'stepByStep';
    if (!tutorConfig.enabledTutorModes[currentMode]) {
      // Current mode is disabled, switch to default or first enabled mode
      const enabledModes = Object.entries(tutorConfig.enabledTutorModes)
        .filter(([_, enabled]) => enabled)
        .map(([mode]) => mode);
      const newMode = tutorConfig.defaultMode && tutorConfig.enabledTutorModes[tutorConfig.defaultMode] 
        ? tutorConfig.defaultMode 
        : enabledModes[0] || 'stepByStep';
      setSettings(s => ({ ...s, tutorMode: newMode }));
    }
  }, [tutorConfig, settings.tutorMode]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('courseContentSettings', JSON.stringify(settings));
  }, [settings]);

  // Helper to change tutor mode AND save to localStorage immediately
  const changeTutorMode = (newMode) => {
    const newSettings = { ...settings, tutorMode: newMode };
    setSettings(newSettings);
    // Save immediately to localStorage (don't wait for useEffect)
    localStorage.setItem('courseContentSettings', JSON.stringify(newSettings));
    console.log('Tutor mode changed to:', newMode);
  };

  // Theme presets
  const themes = {
    dark: {
      name: 'Dark',
      background: '#0d1117',
      headerBg: '#161b22',
      messageBg: '#21262d',
      textColor: '#f0dca0',
      userMsgBg: '#0f4d1a'
    },
    light: {
      name: 'Light',
      background: '#ffffff',
      headerBg: '#f6f8fa',
      messageBg: '#f0f0f0',
      textColor: '#24292f',
      userMsgBg: '#2da44e'
    },
    sepia: {
      name: 'Sepia',
      background: '#f4ecd8',
      headerBg: '#e8dcc8',
      messageBg: '#efe4d0',
      textColor: '#5c4b37',
      userMsgBg: '#8b7355'
    },
    highContrast: {
      name: 'High Contrast',
      background: '#000000',
      headerBg: '#1a1a1a',
      messageBg: '#262626',
      textColor: '#ffffff',
      userMsgBg: '#00aa00'
    }
  };

  const currentTheme = themes[settings.theme] || themes.dark;

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exerciseDropdownRef.current && !exerciseDropdownRef.current.contains(event.target)) {
        setExerciseDropdownOpen(false);
      }
    };
    
    if (exerciseDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [exerciseDropdownOpen]);

  // Text-to-speech functions
  const speakText = (text, messageIndex) => {
    // Stop any current speech
    window.speechSynthesis.cancel();
    
    // Strip code blocks and clean text for reading
    const cleanText = text
      .replace(/```[\s\S]*?```/g, ' (code example) ') // Replace code blocks
      .replace(/`[^`]+`/g, '') // Remove inline code
      .replace(/\*\*/g, '') // Remove bold markers
      .replace(/\[QUIZ_RESULT\][\s\S]*?\[\/QUIZ_RESULT\]/g, '') // Remove quiz tags
      .replace(/[#*_]/g, '') // Remove markdown symbols
      .replace(/\n+/g, '. ') // Convert newlines to pauses
      .trim();
    
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = settings.speechSpeed; // Use global speed setting from settings
    utterance.pitch = 1;
    
    // Update ref BEFORE setting state and starting speech
    currentSpeakingIndexRef.current = messageIndex;
    setSpeakingMessageIndex(messageIndex);
    
    // Only clear state if this utterance is still the current one
    utterance.onend = () => {
      if (currentSpeakingIndexRef.current === messageIndex) {
        setSpeakingMessageIndex(null);
        currentSpeakingIndexRef.current = null;
      }
    };
    utterance.onerror = () => {
      if (currentSpeakingIndexRef.current === messageIndex) {
        setSpeakingMessageIndex(null);
        currentSpeakingIndexRef.current = null;
      }
    };
    
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setSpeakingMessageIndex(null);
    currentSpeakingIndexRef.current = null;
  };

  // Load exercises for a slide from practice file
  const loadExercisesForSlide = async (slideId) => {
    console.log('Loading exercises for slide:', slideId);
    try {
      const response = await fetch(getSlidesProacticesUrl(`${slideId}_practices.txt`) + '?t=' + Date.now());
      console.log('Fetch response status:', response.status, 'for', slideId);
      if (!response.ok) {
        console.log('No practice file found for', slideId);
        setExercises([]);
        return;
      }
      const content = await response.text();
      console.log('Loaded content length:', content.length, 'for', slideId);
      
      // Parse the practice file to extract problems/examples
      const exerciseList = [];
      const lines = content.split('\n');
      let currentExercise = null;
      let currentContent = [];
      
      for (const line of lines) {
        // Match PROBLEM or EXAMPLE headers
        const problemMatch = line.match(/^(PROBLEM|EXAMPLE|CASE STUDY)\s*(\d*):?\s*(.+)/i);
        if (problemMatch) {
          // Save previous exercise if exists
          if (currentExercise) {
            currentExercise.content = currentContent.join('\n').trim();
            exerciseList.push(currentExercise);
          }
          // Start new exercise
          const type = problemMatch[1].toUpperCase();
          const num = problemMatch[2] || exerciseList.length + 1;
          const title = problemMatch[3].trim();
          currentExercise = {
            id: exerciseList.length + 1,
            type: type,
            number: num,
            title: title,
            content: ''
          };
          currentContent = [];
        } else if (currentExercise && !line.match(/^-{3,}/)) {
          // Add content to current exercise (skip separator lines)
          currentContent.push(line);
        }
      }
      
      // Don't forget the last exercise
      if (currentExercise) {
        currentExercise.content = currentContent.join('\n').trim();
        exerciseList.push(currentExercise);
      }
      
      // Post-process: wrap code sections in markdown code fences for proper display
      for (const exercise of exerciseList) {
        exercise.content = wrapCodeSections(exercise.content);
      }
      
      console.log('Parsed exercises for', slideId, ':', exerciseList.map(e => e.title));
      setExercises(exerciseList);
    } catch (error) {
      console.error('Error loading exercises:', error);
      setExercises([]);
    }
  };

  // Start working on a specific exercise
  const startExercise = async (exercise) => {
    setSelectedExercise(exercise);
    setExerciseDropdownOpen(false);
    setMessages([]); // Clear chat for new exercise
    setLoading(true);
    
    logAction?.('Exercise Selected', exercise.title, selectedSlide?.title, null, 'Course Content');

    // Demo mode: show the (static) problem but don't call the AI backend
    if (isDemoMode()) {
      setMessages([
        { role: 'assistant', content: `📝 **${exercise.type} ${exercise.number}: ${exercise.title}**\n\n${exercise.content}\n\n---\n🔒 **Step-by-step AI guidance is locked in demo mode.**\n\nWith a real account, the tutor walks you through this exercise — checking your attempts, explaining mistakes, and giving targeted hints when you're stuck. For example, if your loop used the wrong range, the tutor would say:\n\n> *"Almost! Your loop starts at 1 but the problem expects 0. Try \`range(n)\` instead of \`range(1, n)\`."*\n\n✨ Sign up to solve exercises with the AI tutor!` }
      ]);
      setLoading(false);
      return;
    }

    // Find the index of this slide to know what chapters came before
    const slideIndex = courseContent.findIndex(s => s.id === selectedSlide?.id);
    
    // Load learned chapters only (cheap - only what's needed)
    let learnedContent = '';
    try {
      for (let i = 0; i <= slideIndex; i++) {
        const slideFile = courseContent[i].id + '.txt';
        const response = await fetch(getCurriculumUrl(slideFile));
        if (response.ok) {
          const content = await response.text();
          learnedContent += `\n\n--- Chapter ${i + 1}: ${courseContent[i].title} ---\n${content}`;
        }
      }
    } catch (error) {
      console.error('Error loading curriculum:', error);
    }
    
    // Get future chapter TITLES only (not content - saves tokens!)
    const futureChapterTitles = courseContent
      .slice(slideIndex + 1)
      .map((ch, i) => `- Chapter ${slideIndex + 2 + i}: ${ch.title}`)
      .join('\n');

    // Check if specific concepts are in future chapters (for dynamic warnings)
    const futureChapterTitlesLower = courseContent
      .slice(slideIndex + 1)
      .map(ch => ch.title.toLowerCase());
    
    const listsInFuture = futureChapterTitlesLower.some(t => t.includes('list'));
    const functionsInFuture = futureChapterTitlesLower.some(t => t.includes('function'));
    const dictionariesInFuture = futureChapterTitlesLower.some(t => t.includes('dictionar'));
    const loopsInFuture = futureChapterTitlesLower.some(t => t.includes('loop'));
    const classesInFuture = futureChapterTitlesLower.some(t => t.includes('class') || t.includes('oop') || t.includes('object-oriented'));

    // Build dynamic concept restrictions
    const conceptRestrictions = [
      listsInFuture ? '- "Lists" in future? → NO [] syntax, NO append(), NO list operations, NO ["item1", "item2"]' : null,
      functionsInFuture ? '- "Functions" in future? → NO def, NO return, NO function definitions' : null,
      dictionariesInFuture ? '- "Dictionaries" in future? → NO {} with key:value pairs' : null,
      loopsInFuture ? '- "Loops" in future? → NO for, NO while loops' : null,
      classesInFuture ? '- "Classes" in future? → NO class definitions' : null,
    ].filter(Boolean).join('\n');

    // Only show loop/list warning if Lists is actually in future chapters
    const loopListWarning = listsInFuture ? `

🔴 LOOP EXAMPLES - CRITICAL (Lists chapter is in a FUTURE chapter):
When teaching loops, ALWAYS use range() or string iteration, NEVER lists:
   ❌ WRONG: fruits = ["apple", "banana", "cherry"]
             for fruit in fruits:
   ❌ WRONG: for item in [1, 2, 3]:
   ❌ WRONG: for x in ["a", "b", "c"]:
   
   ✅ CORRECT: for i in range(1, 4):
   ✅ CORRECT: for i in range(3):
   ✅ CORRECT: for char in "hello":
   ✅ CORRECT: for i in range(1, 6):  # iterate 1 to 5` : '';

    const curriculumLockdown = learnedContent 
      ? `\n\n[CURRICULUM LOCKDOWN - CRITICAL - READ CAREFULLY]
You are helping with an exercise from Chapter ${slideIndex + 1}: "${selectedSlide?.title}"

✅ LEARNED CHAPTERS (you CAN use these concepts):
${learnedContent}

🚫 FUTURE CHAPTERS - ABSOLUTELY FORBIDDEN:
${futureChapterTitles || 'None - student has completed all chapters'}

⛔ STRICT RULES - VIOLATION WILL CONFUSE THE STUDENT:
1. ONLY use concepts from LEARNED CHAPTERS above
2. Check each FUTURE CHAPTER TITLE - if a concept is taught there, DO NOT USE IT:
${conceptRestrictions || '   (No specific restrictions - student has learned most concepts)'}
${loopListWarning}

3. This is NOT optional - using future concepts will harm the student's learning
[END CURRICULUM LOCKDOWN]`
      : '';

    // Create exercise-focused system prompt based on tutor mode
    // Read FRESH from localStorage to avoid stale closure issues!
    const savedSettings = JSON.parse(localStorage.getItem('courseContentSettings') || '{}');
    const tutorMode = savedSettings.tutorMode || 'stepByStep';
    console.log('Starting exercise with tutor mode:', tutorMode); // Debug log
    
    const codeFormattingRules = `⚠️ CODE FORMATTING - MANDATORY (DO THIS FIRST):
You MUST wrap ALL Python code in triple backtick code fences with "python" language tag.
Correct format:
\`\`\`python
x = 5
print(x)
\`\`\`

NEVER write code like this (without fences):
x = 5
print(x)

If you don't use code fences, the indentation will break and confuse the student!

⚠️ PYTHON INDENTATION - CRITICAL:
- TOP-LEVEL code (variables, for, while, if, print outside blocks) starts at COLUMN 0 - NO SPACES
- Code INSIDE a block (after :) gets exactly 4 spaces
- When a block ends, return to previous indent level (usually 0)
- CONSECUTIVE statements at the same level must have the SAME indentation

🔴 WRONG - second print has leading spaces:
\`\`\`python
print(ord('ñ'))  # Output: 241
   print(ord('😊'))  # Output: 128522
\`\`\`

✅ CORRECT - both prints start at column 0:
\`\`\`python
print(ord('ñ'))  # Output: 241
print(ord('😊'))  # Output: 128522
\`\`\`

🔴 WRONG INDENTATION (has leading spaces on for loop):
\`\`\`python
x = 1
   for i in range(3):
       print(i)
\`\`\`

✅ CORRECT INDENTATION (for starts at column 0):
\`\`\`python
x = 1
for i in range(3):
    print(i)
\`\`\`

Example - print OUTSIDE the loop (runs once after loop finishes):
\`\`\`python
total = 0
for i in range(1, 6):
    total += i
print(total)
\`\`\`

Example - print INSIDE the loop (runs every iteration):
\`\`\`python
total = 0
for i in range(1, 6):
    total += i
    print(total)
\`\`\`

The indentation level completely changes the program's behavior!`;

    const exerciseDetails = `EXERCISE DETAILS:
${exercise.type} ${exercise.number}: ${exercise.title}
${exercise.content}`;

    let modeInstructions = '';
    
    if (tutorMode === 'stepByStep') {
      modeInstructions = `TUTOR MODE: STEP-BY-STEP DISCOVERY`;
    } else if (tutorMode === 'allSteps') {
      modeInstructions = `TUTOR MODE: SHOW ALL STEPS - Show the complete step breakdown at the start, then work through ONE step at a time.`;
    } else {
      modeInstructions = `TUTOR MODE: ADAPTIVE - Start with step-by-step, switch to showing all steps if student struggles.`;
    }

    // Rule #2 varies by mode - only Step-by-Step mode hides the breakdown
    const rule2 = tutorMode === 'stepByStep' 
      ? `**🚨🚨🚨 RULE #2 IS THE MOST IMPORTANT RULE - NEVER BREAK IT 🚨🚨🚨**
2. **CRITICAL: NEVER GIVE A BREAKDOWN OR LIST OF STEPS!** 
   - When a user presents a NEW problem, DO NOT explain how to solve it!
   - DO NOT say "we can follow these steps: 1... 2... 3..."
   - DO NOT list or preview future steps!
   - Your VERY FIRST response must ONLY be: "That sounds like a great project! To get started, what do you think the first logical step should be?"
   - Then WAIT for the student to suggest step 1 - DO NOT suggest it yourself!`
      : tutorMode === 'allSteps'
      ? `**📋 RULE #2 - SHOW ALL STEPS MODE**
2. **Show the complete step breakdown FIRST:**
   - When a user presents a NEW problem, IMMEDIATELY provide a numbered list of ALL steps needed
   - Example: "Great! Here's how we can break this down:
     Step 1: Get the user's input
     Step 2: Convert to integer
     Step 3: Calculate the result
     Step 4: Display output
     Now let's work through Step 1. How would you code this?"
   - After showing the plan, work through ONE step at a time
   - Do NOT skip steps or combine them`
      : `**🔄 RULE #2 - ADAPTIVE MODE**
2. **Start with discovery, show steps if student struggles:**
   - First ask: "What do you think the first step should be?"
   - If student struggles after 2-3 attempts, offer: "Would you like me to show you the steps?"
   - If yes, show the complete breakdown, then work through one at a time`;

    const systemPrompt = `You are an expert Python programming tutor. Your goal is to help students solve coding problems on their own using a structured, step-by-step Socratic method.

${codeFormattingRules}
${curriculumLockdown}

${exerciseDetails}
${modeInstructions}

**⛔⛔⛔ ABSOLUTE RULE #1 - NEVER SHOW CORRECTED CODE! ⛔⛔⛔**
NEVER write phrases like:
- "Here's how you can write the code:"
- "Let's correct these issues:"
- "Here's the corrected version:"
- "The code should look like this:"
followed by working code. THIS IS THE WORST POSSIBLE TEACHING MISTAKE.
If student's code has 3 errors, point out the errors with QUESTIONS, not solutions!

General Rules:
1. Your primary focus is Python programming.

${rule2}

**🚨🚨🚨 RULE #2.5 - NEVER GIVE AWAY THE ANSWER IN YOUR HINTS! 🚨🚨🚨**
   - When hinting, NEVER include the actual code, values, or specific content from the solution
   - WRONG: "How would you print the headers 'a', 'b', and 'a ** b'?" ← You just gave away the answer!
   - WRONG: "What code would display 'Hello World'?" ← You revealed the string!
   - WRONG: "consider converting it to an integer using int()" ← You revealed int()!
   - WRONG: "you might want to use int() to convert" ← You gave the function name!
   - CORRECT: "What do you think should be printed for the header row?"
   - CORRECT: "What values need to appear in that output?"
   - CORRECT: "input() returns a string. For math operations, what type do we need?"
   - CORRECT: "There's a type consideration here. What does input() return?"
   - Let the STUDENT figure out WHAT to write, not just HOW to write it
   - Your hints should guide their THINKING, not give them the content to type
   
3. You must work on EXACTLY ONE step at a time. Do not move to Step 2 until Step 1 is planned AND coded.

**🚨 CRITICAL: TWO SEPARATE PHASES - DO NOT MIX THEM! 🚨**
4. For the CURRENT step:
   a. **Phase 1 (PLANNING ONLY)**: 
      - Ask the user to describe what the step should do
      - Give hints if they struggle
      - When the step description is finalized, say "Great! Now let's code this step. How would you write the code for this?"
      - **NEVER SHOW CODE DURING PLANNING PHASE!** Not even as an example!
   
   b. **Phase 2 (CODING ONLY)**:
      - Now ask the student to WRITE THE CODE themselves
      - **🚨 CRITICAL: Do NOT complete the step until ALL code for this step is written!**
      - If student writes PARTIAL code (e.g., just "num = input()" when step needs integer), say "Good start! What else do you need?" and let them continue
      - Give hints and feedback on their attempts
      - Only after the student writes the COMPLETE code for the step OR after 5 failed attempts, you may show the correct code
   
   c. **🚨 5-ATTEMPT RULE FOR CODING (MANDATORY) 🚨**:
      - Each piece of code the student submits counts as 1 attempt
      - If attempts < 5: Give HINTS and FEEDBACK, NOT the complete answer!
        * If code is PARTIAL but correct so far: "Good! What's missing?" or "What else does this step need?"
        * If code has errors: Point out the issue, ask them to fix it
      - If attempts >= 5 AND student is not progressing: You may provide the complete answer
      - "Need help" or "I don't know" does NOT skip the 5-attempt requirement - give a hint!

5. After completing a step, ask "What do you think the next step should be?" and WAIT for the student's response.

${tutorMode === 'stepByStep' ? `**🚨 NEVER GIVE A BREAKDOWN - EXAMPLES 🚨**

WRONG - Giving a breakdown when student presents a problem:
Student: "I want to get number of students and find the average"
You: "Great! Let's break it down. We need to: 1. Get number of students 2. Get each score 3. Calculate average..."
❌ THIS IS WRONG! You just gave away the entire solution!

CORRECT - Asking the student to define steps:
Student: "I want to get number of students and find the average"
You: "That sounds like a great project! To get started, what do you think the first logical step should be?"
✅ Then WAIT for the student to suggest a step!` : ''}

**🚨🚨🚨 NEVER GIVE AWAY CONTENT IN HINTS - THIS IS THE #1 RULE! 🚨🚨🚨**

WRONG - Showing the corrected code (THIS IS THE WORST VIOLATION!):
Student writes:
   width = input("Enter the width:")
   length = input("Enter the length:")
   area = length x width
You: "Let's correct these issues. Here's how you can write the code:
   width = float(input('Enter the width:'))
   length = float(input('Enter the length:'))
   area = length * width"
❌❌❌ ABSOLUTELY FORBIDDEN! You just gave the complete solution!

WRONG - Telling them what function to use:
Student writes: num = input("Enter number:")
You: "Good! But you should convert it to an integer using int()"
❌ THIS IS WRONG! You told them the function name!

WRONG - Showing ANY corrected code:
You: "Here's the corrected version:" followed by code
❌ NEVER DO THIS until 5 attempts have failed!

CORRECT - Guide with questions ONLY:
Student writes:
   width = input("Enter the width:")
   area = length x width
You: "I see a couple of things to think about:
   1. I notice a small typo in your prompt - take another look at 'teh'
   2. What symbol does Python use for multiplication? (Hint: it's not the letter x)
   3. input() returns text. For math operations, what needs to happen first?
Can you try fixing these one at a time?"
✅ Ask questions, let THEM figure out the code!

**🚨 CODING PHASE - DO NOT COMPLETE TOO EARLY 🚨**

WRONG - Completing step when code is INCOMPLETE:
Step is: "Get the number of students as an integer"
Student writes: "num = input('Enter number:')"
You: "Good! Now let's move to the next step..."
❌ THIS IS WRONG! The code is missing type conversion!

CORRECT - Keep asking until code is COMPLETE:
Step is: "Get the number of students as an integer"
Student writes: "num = input('Enter number:')"
You: "Good start! You're getting user input. But remember, we need this as a number for calculations. What type does input() return, and how might you change that?"
✅ Do NOT move on until the student fixes the issue!

GENERAL GUIDELINES:
- Be encouraging and supportive
- Point out what's correct in their code
- ALWAYS use \`\`\`python code fences for ANY code
- NEVER provide the full solution until student has made 5 attempts`;

    // Format the exercise problem for display to the user
    const exerciseProblemDisplay = `📝 **${exercise.type} ${exercise.number}: ${exercise.title}**

${exercise.content}`;

    // The user message that will be sent to LLM (includes full problem)
    // Note: exercise.content already has code wrapped in ```python fences from wrapCodeSections
    const userStartMessage = `I want to work on this exercise:

**${exercise.type} ${exercise.number}: ${exercise.title}**
${exercise.content}`;

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4.1',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userStartMessage }
          ],
          temperature: 0,
          max_tokens: 1500
        })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      const assistantMessage = data.choices[0].message.content;

      // Log the exercise start with LLM response
      logAction?.('Exercise Started', exercise.title, assistantMessage, null, 'Course Content');

      // Show: 1) The exercise problem (as assistant intro), 2) Hidden system prompt, 3) User selection, 4) Tutor response
      setMessages([
        { role: 'system', content: systemPrompt },
        { role: 'assistant', content: exerciseProblemDisplay }, // Show the problem to the user first!
        { role: 'user', content: userStartMessage },
        { role: 'assistant', content: assistantMessage }
      ]);
    } catch (error) {
      console.error('Error starting exercise:', error);
      setMessages([
        { role: 'assistant', content: exerciseProblemDisplay }, // Still show the problem even on error
        { role: 'assistant', content: `I encountered an error starting this exercise. Please try again.` }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Enter exercise mode for a slide
  const enterExerciseMode = async (slide) => {
    logAction?.('Exercise Mode Entered', slide.title, '', null, 'Course Content');
    
    // Clear previous state first
    setExercises([]);
    setSelectedExercise(null);
    setMessages([]);
    setExerciseDropdownOpen(false);
    
    // Set new slide
    setExerciseMode(true);
    setSelectedSlide(slide);
    setSelectedTopic(null);
    
    // Load exercises for the new slide
    await loadExercisesForSlide(slide.id);
    
    // Auto-expand dropdown after loading
    setExerciseDropdownOpen(true);
  };

  // Exit exercise mode
  const exitExerciseMode = () => {
    setExerciseMode(false);
    setSelectedExercise(null);
    setExercises([]);
    setExerciseDropdownOpen(false);
  };

  // Auto-scroll when user sends a message - scroll to show user message clearly above input box
  useEffect(() => {
    if (shouldScrollToBottom && chatContainerRef.current) {
      // Small delay to ensure content is rendered before scrolling
      setTimeout(() => {
        if (chatContainerRef.current) {
          const container = chatContainerRef.current;
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      }, 100);
      setShouldScrollToBottom(false);
    }
  }, [messages, shouldScrollToBottom]);

  // Toggle slide expansion
  const toggleSlide = (slideId) => {
    const slide = courseContent.find(s => s.id === slideId);
    const isExpanding = !expandedSlides[slideId];
    logAction?.('Chapter ' + (isExpanding ? 'Expanded' : 'Collapsed'), slide?.title || slideId, '', null, 'Course Content');
    setExpandedSlides(prev => ({
      ...prev,
      [slideId]: !prev[slideId]
    }));
  };

  // Start learning a topic
  const startLearningTopic = async (slide, topic) => {
    // Exit exercise mode if we're in it
    setExerciseMode(false);
    setSelectedExercise(null);
    setExercises([]);
    setExerciseDropdownOpen(false);
    
    setSelectedTopic(topic);
    setSelectedSlide(slide);
    setLastVisitedTopic(topic); // Remember for highlighting when going back
    setMessages([]); // Fresh conversation
    
    logAction?.('Topic Selected', topic, slide.title, null, 'Course Content');

    // Demo mode: AI-generated lessons require login
    if (isDemoMode()) {
      setMessages([
        { role: 'assistant', content: `🔒 **${topic} — AI Lesson Locked**\n\nWith an account, the tutor generates a personalized lesson on **${topic}** — a plain-English explanation, runnable code examples, and a short quiz.\n\nFor example, a lesson on *Variables* might begin:\n\n> *"A variable stores a value you want to reuse. In Python just write \`name = "Alice"\` — no type declaration needed. Then \`print(name)\` outputs \`Alice\`. You can reassign it any time: \`name = "Bob"\`."*\n\nIn demo mode you can still browse the course outline.\n\n✨ Sign up to unlock AI-generated lessons!` }
      ]);
      setLoading(false);
      return;
    }

    // Get username for tracking who generated content
    const username = localStorage.getItem('username') || 'auto';

    // Find the index of this slide to know what chapters came before
    const slideIndex = courseContent.findIndex(s => s.id === slide.id);
    
    // Load curriculum content for this slide and all previous slides (cumulative)
    let cumulativeContent = '';
    try {
      for (let i = 0; i <= slideIndex; i++) {
        const slideFile = courseContent[i].id + '.txt';
        const response = await fetch(getCurriculumUrl(slideFile));
        if (response.ok) {
          const content = await response.text();
          cumulativeContent += `\n\n--- Chapter ${i + 1}: ${courseContent[i].title} ---\n${content}`;
        }
      }
    } catch (error) {
      console.error('Error loading curriculum:', error);
    }

    // Get future chapter titles for lockdown
    const futureChapterTitles = courseContent
      .slice(slideIndex + 1)
      .map((ch, i) => `- Chapter ${slideIndex + 2 + i}: ${ch.title}`)
      .join('\n');

    // Check if specific concepts are in future chapters (for dynamic warnings)
    const futureChapterTitlesLower = courseContent
      .slice(slideIndex + 1)
      .map(ch => ch.title.toLowerCase());
    
    const listsInFuture = futureChapterTitlesLower.some(t => t.includes('list'));
    const functionsInFuture = futureChapterTitlesLower.some(t => t.includes('function'));
    const dictionariesInFuture = futureChapterTitlesLower.some(t => t.includes('dictionar'));
    const loopsInFuture = futureChapterTitlesLower.some(t => t.includes('loop'));
    const classesInFuture = futureChapterTitlesLower.some(t => t.includes('class') || t.includes('oop') || t.includes('object-oriented'));

    // Build dynamic concept restrictions
    const conceptRestrictions = [
      listsInFuture ? '- "Lists" in future? → NO [] syntax, NO append(), NO list operations, NO ["item1", "item2"]' : null,
      functionsInFuture ? '- "Functions" in future? → NO def, NO return, NO function definitions' : null,
      dictionariesInFuture ? '- "Dictionaries" in future? → NO {} with key:value pairs' : null,
      loopsInFuture ? '- "Loops" in future? → NO for, NO while loops' : null,
      classesInFuture ? '- "Classes" in future? → NO class definitions' : null,
    ].filter(Boolean).join('\n');

    // Only show loop/list warning if Lists is actually in future chapters
    const loopListWarning = listsInFuture ? `

🔴 LOOP EXAMPLES - CRITICAL (Lists chapter is in a FUTURE chapter):
When teaching loops, ALWAYS use range() or string iteration, NEVER lists:
   ❌ WRONG: fruits = ["apple", "banana", "cherry"]
             for fruit in fruits:
   ❌ WRONG: for item in [1, 2, 3]:
   ❌ WRONG: for x in ["a", "b", "c"]:
   
   ✅ CORRECT: for i in range(1, 4):
   ✅ CORRECT: for i in range(3):
   ✅ CORRECT: for char in "hello":
   ✅ CORRECT: for i in range(1, 6):  # iterate 1 to 5` : '';

    // Build curriculum lockdown section
    const curriculumLockdown = cumulativeContent 
      ? `\n\n[CURRICULUM LOCKDOWN - CRITICAL - READ CAREFULLY]
You are teaching Chapter ${slideIndex + 1}: "${slide.title}"

✅ LEARNED CHAPTERS (you CAN use these concepts):
${cumulativeContent}

🚫 FUTURE CHAPTERS - ABSOLUTELY FORBIDDEN:
${futureChapterTitles || 'None - student has completed all chapters'}

⛔ STRICT RULES - VIOLATION WILL CONFUSE THE STUDENT:
1. ONLY use concepts from LEARNED CHAPTERS above
2. Check each FUTURE CHAPTER TITLE - if a concept is taught there, DO NOT USE IT:
${conceptRestrictions || '   (No specific restrictions - student has learned most concepts)'}
${loopListWarning}

3. If the student asks about an ADVANCED topic from a later chapter, respond:
   "That's a great question! That topic is covered in a later chapter. For now, we're focusing on Chapter ${slideIndex + 1}.
   Would you like a quick preview (no coding yet) or stay focused on the current chapter?"

[END CURRICULUM LOCKDOWN]`
      : '';

    // Create the teaching system prompt
    const systemPrompt = `You are an expert Python tutor helping a student learn "${topic}" from the chapter "${slide.title}".
${curriculumLockdown}

YOUR TEACHING FLOW:

## STEP 1: TEACH (do this first)
- **Introduction** (6-8 sentences): What this topic is, why it matters, and real-world applications.
- **Core Concepts** (4-6 paragraphs): Explain clearly with simple language, analogies, and common pitfalls to avoid.
- **Code Examples**: Show 6-8 examples (10-20 lines each) with expected output and detailed explanations.

## STEP 2: OFFER PRACTICE (after teaching)
End your teaching with this exact question:
"🎯 **Ready to practice?** Would you like:
1. **Quiz** - Test your understanding with multiple choice questions
2. **Coding Exercise** - Try writing some simple code
3. **Both** - Do quiz first, then coding

Just reply with 1, 2, 3, or ask me any questions!"

## STEP 3: PRACTICE MODE (based on student's choice)

**If student chooses QUIZ:**
- Give ONE question at a time
- Wait for their answer before showing the next question
- After they answer, say if correct/incorrect with brief explanation
- Then ask: "Ready for the next question?" or "Want to try a coding exercise now?"
- Have 2-3 quiz questions ready

**If student chooses CODING:**
- Give ONE simple exercise at a time (1-5 lines of code)
- Clearly state what the program should do and expected output
- After they submit code, review it and give feedback
- Then ask: "Want another exercise?" or "Want to try some quiz questions?"
- Have 2 exercises ready (easy → medium difficulty)

## HINT SYSTEM (VERY IMPORTANT):
When a student asks for help, hints, or says "I don't know", "I'm stuck", "help me", etc.:

**For QUIZ questions:**
- Hint 1: Give a general hint about the concept (don't reveal the answer)
- Hint 2: Give a more specific hint, maybe eliminate one wrong option
- Hint 3: If they still need help, reveal the answer with explanation

**For CODING exercises:**
🚨 NEVER GIVE THE FULL CODE! Use Socratic guidance instead:
- Hint 1: Explain the approach/algorithm in words (NO CODE)
- Hint 2: Ask guiding questions like "What do you need first?", "How would you get input?"
- Hint 3: Give pseudocode or describe the steps in plain English
- Hint 4: Show ONLY the first line of code, ask them to continue
- Hint 5+: If truly stuck after many attempts, show ONE line at a time, not the whole solution

🚨 WRONG (giving full solution):
"Here's the solution:
\`\`\`python
width = float(input('Enter width:'))
length = float(input('Enter length:'))
area = width * length
print(area)
\`\`\`"

✅ CORRECT (guiding without giving code):
"Let's break this down:
1. What do you need to get from the user first?
2. Once you have those values, what operation calculates area?
3. Try writing the first line - how would you get the width?"

ALWAYS guide their THINKING, not give them code to copy!

ALWAYS track hint count mentally. Say things like:
- "Here's a hint: ..." (hint 1)
- "Let me guide you: ..." (hint 2)  
- "Let's work through this together..." (hint 3+)

NEVER give the full solution on ANY request for help!

IMPORTANT RULES:
- NEVER dump multiple questions at once
- ONE question or exercise at a time only
- Always wait for student response before continuing
- Be encouraging and supportive
- Use [QUIZ_RESULT] tags when grading
- Keep everything beginner-friendly
- Progressive hints before solutions!

CODE FORMATTING (CRITICAL - MUST FOLLOW):
When showing ANY Python code, you MUST use markdown code fences. Write three backtick characters, then "python", then newline, then your code, then newline, then three backtick characters.

When showing program OUTPUT, you MUST also use code fences. Write three backtick characters, then newline, then the output, then newline, then three backtick characters. Label it clearly as "Output:" before the code fence.

Like this format:

\`\`\`python
for i in range(3):
    print(i)
\`\`\`

NEVER write code without the triple backtick fence. This is required for proper display.

⚠️ PYTHON INDENTATION - CRITICAL:
- TOP-LEVEL code (variables, for, while, if, print outside blocks) starts at COLUMN 0 - NO SPACES
- Code INSIDE a block (after :) gets exactly 4 spaces
- When a block ends, return to previous indent level (usually 0)
- CONSECUTIVE statements at the same level must have the SAME indentation

🔴 WRONG - second print has leading spaces:
\`\`\`python
print(ord('ñ'))  # Output: 241
   print(ord('😊'))  # Output: 128522
\`\`\`

✅ CORRECT - both prints start at column 0:
\`\`\`python
print(ord('ñ'))  # Output: 241
print(ord('😊'))  # Output: 128522
\`\`\`

🔴 WRONG INDENTATION (has leading spaces on for loop):
\`\`\`python
x = 1
   for i in range(3):
       print(i)
\`\`\`

✅ CORRECT INDENTATION (for starts at column 0):
\`\`\`python
x = 1
for i in range(3):
    print(i)
\`\`\`

Start teaching now with Step 1!`;

    // Check if content exists in database first
    setLoading(true);
    try {
      const cachedContent = await getCourseContent(slide.id, topic);
      
      if (cachedContent && cachedContent.content) {
        // Content found in DB - use it directly
        console.log('[CourseContentView] Using cached content from DB for:', slide.id, topic);
        logAction?.('Topic Content Loaded from Cache', topic, slide.title, null, 'Course Content');
        
        setMessages([
          { role: 'system', content: systemPrompt },
          { role: 'user', content: `Please teach me about "${topic}". Start from the basics.` },
          { role: 'assistant', content: cachedContent.content }
        ]);
        setLoading(false);
        return;
      }
      
      // Content not found - generate via LLM
      console.log('[CourseContentView] No cached content, generating via LLM for:', slide.id, topic);
      
      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4.1',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: `Please teach me about "${topic}". Start from the basics.` }
          ],
          temperature: 0.4,
          max_tokens: 4000
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      const assistantMessage = data.choices[0].message.content;

      // Save generated content to database for future use
      const saved = await saveCourseContent(slide.id, topic, assistantMessage, username);
      if (saved) {
        console.log('[CourseContentView] Content saved to DB for:', slide.id, topic);
        logAction?.('Topic Content Generated and Saved', topic, slide.title, null, 'Course Content');
      } else {
        console.warn('[CourseContentView] Failed to save content to DB');
      }

      setMessages([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `Please teach me about "${topic}". Start from the basics.` },
        { role: 'assistant', content: assistantMessage }
      ]);

    } catch (error) {
      console.error('Error starting topic:', error);
      setMessages([
        { role: 'assistant', content: `I'd be happy to teach you about "${topic}"! However, I encountered an error. Please try again.` }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Send a message directly (used by Ask AI button in code runner)
  const sendMessageDirect = async (messageText) => {
    if (!messageText.trim() || loading) return;

    const userMessage = messageText.trim();
    
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setShouldScrollToBottom(true); // Scroll immediately when user sends message
    setLoading(true);

    try {
      const systemMessage = newMessages.find(m => m.role === 'system');
      const nonSystemMessages = newMessages.filter(m => m.role !== 'system');
      const recentMessages = nonSystemMessages.slice(-15);
      
      // Count attempts for hint progression
      const attemptCount = countStudentAttempts(recentMessages);
      attemptCountRef.current = attemptCount;
      
      // Inject attempt tracking into system prompt if in exercise mode
      let finalSystemContent = systemMessage?.content || '';
      if (exerciseMode && systemMessage) {
        const attemptTracking = `

[STUDENT ATTEMPT TRACKING]
Current student attempts for this exercise step: ${attemptCount}
- If attempts < 5: Give HINTS only, NOT the answer
- If attempts >= 5: You may now provide the solution if student is still stuck
- Student asking "help" or "hint" does NOT count as an attempt
[END ATTEMPT TRACKING]`;
        
        if (finalSystemContent.includes('[STUDENT ATTEMPT TRACKING]')) {
          finalSystemContent = finalSystemContent.replace(
            /\[STUDENT ATTEMPT TRACKING\][\s\S]*?\[END ATTEMPT TRACKING\]/,
            attemptTracking.trim()
          );
        } else {
          finalSystemContent = finalSystemContent + attemptTracking;
        }
      }
      
      const apiMessages = [
        ...(systemMessage ? [{ role: systemMessage.role, content: finalSystemContent }] : []),
        ...recentMessages.map(m => ({ role: m.role, content: m.content }))
      ];

      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: apiMessages,
          temperature: 0.4,
          max_tokens: 1500
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      const assistantMessage = data.choices[0].message.content;

      // Log the chat exchange (user message + tutor response)
      const context = exerciseMode 
        ? `Exercise: ${selectedExercise?.title || 'Unknown'}` 
        : `Topic: ${selectedTopic || 'Unknown'}`;
      logAction?.('Chat Exchange', `${context} | User: ${messageText}`, assistantMessage, null, 'Course Content');
      
      // Build final messages array including assistant response
      const finalMessages = [...newMessages, { role: 'assistant', content: assistantMessage }];
      
      // Background struggle detection when student sends code from exercise
      // Send conversation WITHOUT tutor's response (it can confuse the analysis with teaching content)
      if (messageText.includes('```python') || messageText.includes('my code:')) {
        const topicForAnalysis = exerciseMode 
          ? (selectedExercise?.title || 'Exercise') 
          : (selectedTopic || 'Course Content');
        // Extract code from message
        const codeMatch = messageText.match(/```python\n([\s\S]*?)```/);
        const codeToAnalyze = codeMatch ? codeMatch[1] : messageText;
        // Fire and forget - don't await
        // Pass newMessages (without tutor's response) to avoid confusion
        analyzeStruggleInBackground(username, topicForAnalysis, codeToAnalyze, newMessages);
      }

      setMessages(finalMessages);
      setShouldScrollToBottom(true);

    } catch (error) {
      console.error('Error:', error);
      setMessages([...newMessages, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  // Send a message in the chat
  const sendMessage = async () => {
    if (!inputValue.trim() || loading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setShouldScrollToBottom(true); // Scroll immediately when user sends message
    setLoading(true);

    try {
      // Keep system prompt + last 15 messages (sliding window to manage token usage)
      const systemMessage = newMessages.find(m => m.role === 'system');
      const nonSystemMessages = newMessages.filter(m => m.role !== 'system');
      const recentMessages = nonSystemMessages.slice(-15); // Last 15 messages
      
      // Count attempts for hint progression
      const attemptCount = countStudentAttempts(recentMessages);
      attemptCountRef.current = attemptCount;
      
      // Inject attempt tracking into system prompt if in exercise mode
      let finalSystemContent = systemMessage?.content || '';
      if (exerciseMode && systemMessage) {
        const attemptTracking = `

[STUDENT ATTEMPT TRACKING]
Current student attempts for this exercise step: ${attemptCount}
- If attempts < 5: Give HINTS only, NOT the answer
- If attempts >= 5: You may now provide the solution if student is still stuck
- Student asking "help" or "hint" does NOT count as an attempt
[END ATTEMPT TRACKING]`;
        
        // Add attempt tracking if not already present (or update it)
        if (finalSystemContent.includes('[STUDENT ATTEMPT TRACKING]')) {
          finalSystemContent = finalSystemContent.replace(
            /\[STUDENT ATTEMPT TRACKING\][\s\S]*?\[END ATTEMPT TRACKING\]/,
            attemptTracking.trim()
          );
        } else {
          finalSystemContent = finalSystemContent + attemptTracking;
        }
      }
      
      const apiMessages = [
        ...(systemMessage ? [{ role: systemMessage.role, content: finalSystemContent }] : []),
        ...recentMessages.map(m => ({ role: m.role, content: m.content }))
      ];

      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4.1',
          messages: apiMessages,
          temperature: 0,
          max_tokens: 1500
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      const assistantMessage = data.choices[0].message.content;

      // Log the chat exchange (user message + tutor response)
      const context = exerciseMode 
        ? `Exercise: ${selectedExercise?.title || 'Unknown'}` 
        : `Topic: ${selectedTopic || 'Unknown'}`;
      logAction?.('Chat Exchange', `${context} | User: ${userMessage}`, assistantMessage, null, 'Course Content');

      setMessages([...newMessages, { role: 'assistant', content: assistantMessage }]);
      setShouldScrollToBottom(true); // Scroll AFTER LLM response is received

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages([...newMessages, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
      setShouldScrollToBottom(true); // Also scroll on error
    } finally {
      setLoading(false);
    }
  };

  // Handle keyboard shortcuts in input
  const handleKeyDown = (e) => {
    // Ctrl+Enter or Cmd+Enter to send
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Auto-grow textarea
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  };

  // Go back to topic selection
  const backToTopics = () => {
    logAction?.('Back to Topics', selectedSlide?.title, '', null, 'Course Content');
    setSelectedTopic(null);
    // Keep selectedSlide so the chapter stays highlighted/expanded
    setMessages([]);
    setSidebarCollapsed(false); // Reset sidebar when going back
    exitExerciseMode();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: currentTheme.background,
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100vh',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        backgroundColor: currentTheme.headerBg,
        borderBottom: '1px solid #30363d',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        zIndex: 500,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '28px' }}>📚</span>
          <div>
            <h1 style={{ margin: 0, color: currentTheme.textColor, fontSize: '22px' }}>Course Content</h1>
            <p style={{ margin: 0, color: currentTheme.textColor, opacity: 0.7, fontSize: '14px' }}>
              {exerciseMode 
                ? `Exercises: ${selectedSlide?.title}` 
                : selectedTopic 
                  ? `Learning: ${selectedTopic}` 
                  : 'Select a topic to start learning'}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Scratch Pad Button - only show when in a topic or exercise */}
          {(selectedTopic || exerciseMode) && (
          <button
            onClick={() => setShowScratchPad(true)}
            style={{
              padding: '10px 16px',
              backgroundColor: '#238636',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            💻 Scratch Pad
          </button>
          )}
          {/* Settings Button - only show when in a topic or exercise */}
          {(selectedTopic || exerciseMode) && (
          <button
            onClick={() => setShowSettings(true)}
            style={{
              padding: '10px 16px',
              backgroundColor: '#21262d',
              color: '#c9d1d9',
              border: '2px solid #a89860',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            ⚙️ Settings
          </button>
          )}
          {(selectedTopic || exerciseMode) && (
            <button
              onClick={backToTopics}
              style={{
                padding: '10px 20px',
                backgroundColor: '#21262d',
                color: '#c9d1d9',
                border: '1px solid #30363d',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              ← Back to Topics
            </button>
          )}
          <button
            onClick={() => {
              // Reset main window scroll before closing
              window.scrollTo(0, 0);
              onClose();
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#21262d',
              color: '#c9d1d9',
              border: '1px solid #30363d',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onMouseOver={(e) => { e.target.style.backgroundColor = '#f85149'; e.target.style.color = '#ffffff'; e.target.style.borderColor = '#f85149'; }}
            onMouseOut={(e) => { e.target.style.backgroundColor = '#21262d'; e.target.style.color = '#c9d1d9'; e.target.style.borderColor = '#30363d'; }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Sidebar Toggle Button - outside the sidebar so it's always visible */}
        {(selectedTopic || exerciseMode) && (
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{
              position: 'absolute',
              left: sidebarCollapsed ? '5px' : '305px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '24px',
              height: '50px',
              backgroundColor: sidebarCollapsed ? '#30363d' : '#238636',
              border: 'none',
              borderRadius: '0 8px 8px 0',
              cursor: 'pointer',
              color: '#fff',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 200,
              boxShadow: '2px 0 8px rgba(0,0,0,0.3)',
              transition: 'left 0.3s ease, background-color 0.3s ease'
            }}
            title={sidebarCollapsed ? 'Show chapters' : 'Hide chapters'}
          >
            {sidebarCollapsed ? '▶' : '◀'}
          </button>
        )}

        {/* Left Panel - Topics List */}
        <div style={{
          width: (selectedTopic || exerciseMode) 
            ? (sidebarCollapsed ? '0px' : '320px') 
            : '100%',
          minWidth: (selectedTopic || exerciseMode) && !sidebarCollapsed ? '320px' : '0px',
          maxWidth: (selectedTopic || exerciseMode) ? '320px' : '1200px',
          margin: (selectedTopic || exerciseMode) ? 0 : '0 auto',
          backgroundColor: '#161b22',
          borderRight: (selectedTopic || exerciseMode) && !sidebarCollapsed ? '1px solid #30363d' : 'none',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: sidebarCollapsed ? '0px' : '20px',
          transition: 'all 0.3s ease'
        }}>
          {!sidebarCollapsed && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {courseContentLoading && (
              <div style={{ color: '#8b949e', textAlign: 'center', padding: '40px' }}>
                📚 Loading course content...
              </div>
            )}
            {courseContentError && (
              <div style={{ color: '#f85149', textAlign: 'center', padding: '40px' }}>
                ⚠️ {courseContentError}
              </div>
            )}
            {!courseContentLoading && !courseContentError && courseContent.length === 0 && (
              <div style={{ color: '#8b949e', textAlign: 'center', padding: '40px' }}>
                No chapters found. Add entries to course_structure.json
              </div>
            )}
            {courseContent.map((slide, slideIndex) => (
              <div 
                key={slide.id}
                style={{
                  backgroundColor: '#21262d',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: selectedSlide?.id === slide.id ? '2px solid #58a6ff' : '1px solid #30363d'
                }}
              >
                {/* Slide Header */}
                <div
                  onClick={() => toggleSlide(slide.id)}
                  style={{
                    padding: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: expandedSlides[slide.id] ? '#30363d' : '#21262d',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!expandedSlides[slide.id]) {
                      e.currentTarget.style.backgroundColor = '#2d333b';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedSlides[slide.id]) {
                      e.currentTarget.style.backgroundColor = '#21262d';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Chapter completion checkbox - cycles: empty -> ✓ studied -> ⊘ skipped -> empty */}
                    <span
                      onClick={(e) => toggleChapterComplete(e, slide.id)}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        border: getChapterStatus(slide) === 'studied' ? '2px solid #3fb950' 
                             : getChapterStatus(slide) === 'skipped' ? '2px solid #f0883e' 
                             : '2px solid #6e7681',
                        backgroundColor: getChapterStatus(slide) === 'studied' ? '#238636' 
                                       : getChapterStatus(slide) === 'skipped' ? '#9e6a03' 
                                       : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        flexShrink: 0,
                        marginRight: '4px'
                      }}
                      title={getChapterStatus(slide) === 'studied' ? 'Click to mark as skipped' 
                           : getChapterStatus(slide) === 'skipped' ? 'Click to clear' 
                           : 'Click to mark as studied (click again for skipped)'}
                    >
                      {getChapterStatus(slide) === 'studied' && (
                        <span style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                      )}
                      {getChapterStatus(slide) === 'skipped' && (
                        <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>⊘</span>
                      )}
                    </span>
                    <span style={{
                      backgroundColor: getChapterStatus(slide) === 'skipped' ? '#6e7681' : '#238636',
                      color: '#fff',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      opacity: getChapterStatus(slide) === 'skipped' ? 0.6 : 1
                    }}>
                      {slideIndex + 1}
                    </span>
                    <span style={{ 
                      color: getChapterStatus(slide) === 'studied' ? '#3fb950' 
                           : getChapterStatus(slide) === 'skipped' ? '#8b949e' 
                           : '#fff', 
                      fontWeight: '600',
                      fontSize: (selectedTopic || exerciseMode) ? '15px' : '17px',
                      textDecoration: getChapterStatus(slide) === 'skipped' ? 'line-through' : 'none',
                      fontStyle: getChapterStatus(slide) === 'skipped' ? 'italic' : 'normal'
                    }}>
                      {slide.title}
                    </span>
                    {/* Progress indicator */}
                    {slide.topics && slide.topics.length > 0 && (() => {
                      const counts = getCompletedTopicsCount(slide);
                      return (
                      <span style={{
                        color: '#8b949e',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: '#30363d',
                        padding: '3px 10px',
                        borderRadius: '10px'
                      }}>
                        {counts.studied > 0 && (
                          <span style={{ color: '#3fb950' }}>✓{counts.studied}</span>
                        )}
                        {counts.studied > 0 && counts.skipped > 0 && ' '}
                        {counts.skipped > 0 && (
                          <span style={{ color: '#f0883e' }}>⊘{counts.skipped}</span>
                        )}
                        {counts.studied === 0 && counts.skipped === 0 && (
                          <span>0/{slide.topics.length}</span>
                        )}
                        {(counts.studied > 0 || counts.skipped > 0) && (
                          <span style={{ color: '#6e7681' }}>/{slide.topics.length}</span>
                        )}
                      </span>
                    );
                    })()}
                  </div>
                  <span style={{ 
                    color: '#8b949e', 
                    transform: expandedSlides[slide.id] ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.2s'
                  }}>
                    ▼
                  </span>
                </div>

                {/* Topics List */}
                {expandedSlides[slide.id] && (
                  <div style={{ padding: '0 16px 16px 16px' }}>
                    {slide.topics.map((topic, topicIndex) => {
                      const isHighlighted = selectedTopic === topic || (!selectedTopic && lastVisitedTopic === topic);
                      const topicStatus = normalizeStatus(studyProgress.topics[`${slide.id}-${topicIndex}`]); // 'studied', 'skipped', or null
                      return (
                      <div
                        key={topicIndex}
                        onClick={() => startLearningTopic(slide, topic)}
                        style={{
                          padding: '10px 12px',
                          margin: '4px 0',
                          backgroundColor: isHighlighted ? '#388bfd30' : '#161b22',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          color: isHighlighted ? '#58a6ff' 
                               : topicStatus === 'studied' ? '#3fb950' 
                               : topicStatus === 'skipped' ? '#8b949e' 
                               : '#c9d1d9',
                          fontSize: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s',
                          border: isHighlighted ? '1px solid #58a6ff' : '1px solid transparent',
                          opacity: topicStatus === 'skipped' ? 0.6 : 1,
                          textDecoration: topicStatus === 'skipped' ? 'line-through' : 'none',
                          fontStyle: topicStatus === 'skipped' ? 'italic' : 'normal'
                        }}
                        onMouseEnter={(e) => {
                          if (!isHighlighted) {
                            e.currentTarget.style.backgroundColor = '#30363d';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isHighlighted) {
                            e.currentTarget.style.backgroundColor = '#161b22';
                          }
                        }}
                      >
                        {/* Topic completion checkbox - cycles: empty -> ✓ studied -> ⊘ skipped -> empty */}
                        <span
                          onClick={(e) => toggleTopicComplete(e, slide.id, topicIndex)}
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px',
                            border: topicStatus === 'studied' ? '2px solid #3fb950' 
                                 : topicStatus === 'skipped' ? '2px solid #f0883e' 
                                 : '2px solid #6e7681',
                            backgroundColor: topicStatus === 'studied' ? '#238636' 
                                           : topicStatus === 'skipped' ? '#9e6a03' 
                                           : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            flexShrink: 0,
                            marginRight: '8px',
                            padding: '2px'
                          }}
                          title={topicStatus === 'studied' ? 'Click to mark as skipped' 
                               : topicStatus === 'skipped' ? 'Click to clear' 
                               : 'Click to mark as studied (click again for skipped)'}
                        >
                          {topicStatus === 'studied' && (
                            <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                          )}
                          {topicStatus === 'skipped' && (
                            <span style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }}>⊘</span>
                          )}
                        </span>
                        <span style={{ color: '#8b949e', marginRight: '6px' }}>
                          {topicStatus === 'studied' ? '✅' : topicStatus === 'skipped' ? '⏭️' : '📖'}
                        </span>
                        <span>{topic}</span>
                      </div>
                    );
                    })}
                    
                    {/* Programming Exercises Link */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        enterExerciseMode(slide);
                      }}
                      style={{
                        padding: '12px 14px',
                        margin: '12px 0 4px 0',
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: exerciseMode && selectedSlide?.id === slide.id ? '#a371f7' : '#d2a8ff',
                        fontSize: '13px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '10px',
                        transition: 'all 0.2s',
                        border: exerciseMode && selectedSlide?.id === slide.id ? '1px solid #a371f7' : '1px solid #8957e5',
                        width: 'fit-content'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#3d2a5e';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span>📝</span>
                      Programming Exercises
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
        </div>

        {/* Right Panel - Chat/Tutoring */}
        {(selectedTopic || exerciseMode) && (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: currentTheme.background,
            marginLeft: sidebarCollapsed ? '35px' : '0px',
            transition: 'margin-left 0.3s ease',
            minHeight: 0,
            overflow: 'hidden',
          }}>
            {/* Exercise Mode Header with Dropdown */}
            {exerciseMode && (
              <div style={{
                padding: '16px 20px',
                backgroundColor: currentTheme.headerBg,
                borderBottom: '1px solid #30363d',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexShrink: 0,
              }}>
                <span style={{ fontSize: '24px' }}>📝</span>
                <div ref={exerciseDropdownRef} style={{ flex: 1, position: 'relative' }}>
                  <button
                    onClick={() => setExerciseDropdownOpen(!exerciseDropdownOpen)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: '#21262d',
                      border: '1px solid #8957e5',
                      borderRadius: '8px',
                      color: '#d2a8ff',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      textAlign: 'left'
                    }}
                  >
                    <span>
                      {selectedExercise 
                        ? `${selectedExercise.type} ${selectedExercise.number}: ${selectedExercise.title}`
                        : '▼ Select an Exercise to Work On'}
                    </span>
                    <span style={{ 
                      transform: exerciseDropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.2s'
                    }}>▼</span>
                  </button>
                  
                  {/* Dropdown List */}
                  {exerciseDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '4px',
                      backgroundColor: '#21262d',
                      border: '1px solid #30363d',
                      borderRadius: '8px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 100,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                    }}>
                      {exercises.length === 0 ? (
                        <div style={{ padding: '16px', color: '#8b949e', textAlign: 'center' }}>
                          No exercises available for this slide
                        </div>
                      ) : (
                        exercises.map((exercise) => (
                          <div
                            key={`${selectedSlide?.id}-${exercise.id}`}
                            onClick={() => startExercise(exercise)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #30363d',
                              backgroundColor: 'transparent',
                              borderLeft: selectedExercise?.id === exercise.id ? '3px solid #8957e5' : '3px solid transparent',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              transition: 'border-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedExercise?.id !== exercise.id) {
                                e.currentTarget.style.backgroundColor = '#30363d';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <span style={{
                              backgroundColor: exercise.type === 'PROBLEM' ? '#8957e5' : 
                                             exercise.type === 'CASE STUDY' ? '#f0883e' : '#238636',
                              color: '#fff',
                              padding: '3px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              minWidth: '70px',
                              textAlign: 'center'
                            }}>
                              {exercise.type}
                            </span>
                            <span style={{ 
                              color: selectedExercise?.id === exercise.id ? '#a371f7' : '#c9d1d9',
                              fontSize: '13px'
                            }}>
                              {exercise.title}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Chat Messages */}
            <div 
              ref={chatContainerRef}
              style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {/* Show placeholder if in exercise mode but no exercise selected */}
              {exerciseMode && !selectedExercise && messages.length === 0 && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#8b949e',
                  textAlign: 'center',
                  padding: '40px'
                }}>
                  <span style={{ fontSize: '48px', marginBottom: '16px' }}>👆</span>
                  <p style={{ fontSize: '16px', marginBottom: '8px' }}>
                    Select an exercise from the dropdown above
                  </p>
                  <p style={{ fontSize: '14px', color: '#6e7681' }}>
                    {exercises.length} exercises available for {selectedSlide?.title}
                  </p>
                </div>
              )}
              
              {messages.filter(m => m.role !== 'system').map((msg, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start'
                  }}
                >
                  <div style={{
                    maxWidth: '85%',
                    minWidth: exerciseMode ? '10%' : 'auto',
                    width: (exerciseMode && msg.role === 'assistant') ? '85%' : 'auto',
                    padding: '14px 18px',
                    paddingTop: msg.role === 'assistant' ? (settings.speechEnabled ? '55px' : '14px') : '14px', // Extra space for Listen button only if speech enabled
                    borderRadius: exerciseMode ? '12px' : (msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px'),
                    backgroundColor: msg.role === 'user' ? currentTheme.userMsgBg : currentTheme.messageBg,
                    color: msg.role === 'user' ? '#fff' : currentTheme.textColor,
                    fontSize: `${settings.fontSize}px`,
                    lineHeight: '1.6',
                    position: 'relative',
                    borderLeft: exerciseMode ? (msg.role === 'user' ? `4px solid ${currentTheme.userMsgBg}` : '4px solid #8957e5') : 'none'
                  }}>
                    {msg.role === 'assistant' ? (
                      <>
                        {/* Read Aloud Button - positioned at top, only if speech enabled */}
                        {settings.speechEnabled && (
                          <button
                            onClick={() => speakingMessageIndex === index ? stopSpeaking() : speakText(msg.content, index)}
                            title={speakingMessageIndex === index ? 'Stop reading' : 'Read aloud'}
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '10px',
                              background: speakingMessageIndex === index ? '#f85149' : '#fff3b0',
                              border: 'none',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              padding: '8px 14px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              color: speakingMessageIndex === index ? '#fff' : '#000',
                              fontSize: '13px',
                              fontWeight: '600',
                              transition: 'all 0.2s',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                          >
                            <span style={{ fontSize: '16px' }}>{speakingMessageIndex === index ? '⏹️' : '🔊'}</span>
                            {speakingMessageIndex === index ? 'Stop' : 'Listen'}
                          </button>
                        )}
                        <MessageContent content={renderMessageContent(msg.content)} showTryIt={true} onSendToChat={sendMessageDirect} />
                      </>
                    ) : (
                      <MessageContent content={renderMessageContent(msg.content)} showTryIt={false} />
                    )}
                  </div>
                </div>
              ))}
              
              {loading && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-start'
                }}>
                  <div style={{
                    padding: '14px 18px',
                    borderRadius: '18px 18px 18px 4px',
                    backgroundColor: '#21262d',
                    color: '#8b949e',
                    fontSize: '14px'
                  }}>
                    <ThinkingIndicator />
                  </div>
                </div>
              )}
              
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div style={{
              padding: '16px 20px',
              backgroundColor: '#161b22',
              borderTop: '1px solid #30363d',
              flexShrink: 0,
            }}>
              <div style={{ width: '85%' }}>
                <textarea
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your answer or code here..."
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: '#21262d',
                    border: '1px solid #6e6b60',
                    borderRadius: '12px',
                    color: '#fff',
                    fontSize: '14px',
                    resize: 'none',
                    minHeight: '80px',
                    maxHeight: '200px',
                    outline: 'none',
                    fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                    lineHeight: '1.5',
                    boxSizing: 'border-box'
                  }}
                  rows={3}
                />
              </div>
              <div style={{ marginTop: '10px', textAlign: 'left' }}>
                <button
                  onClick={sendMessage}
                  disabled={loading || !inputValue.trim()}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: loading || !inputValue.trim() ? '#21262d' : '#238636',
                    color: loading || !inputValue.trim() ? '#8b949e' : '#fff',
                    border: '3px solid #a89860',
                    borderRadius: '10px',
                    cursor: loading || !inputValue.trim() ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    height: '44px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {loading ? '...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scratch Pad Modal */}
      {showScratchPad && (
        <CodeRunnerModal
          initialCode=""
          title="Scratch Pad"
          onClose={() => setShowScratchPad(false)}
          onSendToChat={(message) => {
            setShowScratchPad(false);
            sendMessageDirect(message);
          }}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#161b22',
            borderRadius: '12px',
            border: '2px solid #a89860',
            width: '90%',
            maxWidth: '450px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{ margin: 0, color: '#f0dca0', fontSize: '20px' }}>⚙️ Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#c9d1d9',
                  fontSize: '24px',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => e.target.style.color = '#f85149'}
                onMouseOut={(e) => e.target.style.color = '#c9d1d9'}
              >
                ×
              </button>
            </div>

            {/* Speech Settings */}
            <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid #555d66', borderRadius: '8px' }}>
              <h3 style={{ color: '#58a6ff', fontSize: '16px', marginBottom: '12px', marginTop: 0 }}>🔊 Speech</h3>
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <span style={{ color: '#c9d1d9' }}>Enable Read Aloud</span>
                <button
                  onClick={() => setSettings(s => ({ ...s, speechEnabled: !s.speechEnabled }))}
                  style={{
                    width: '50px',
                    height: '26px',
                    borderRadius: '13px',
                    border: 'none',
                    backgroundColor: settings.speechEnabled ? '#238636' : '#21262d',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    position: 'absolute',
                    top: '2px',
                    left: settings.speechEnabled ? '26px' : '2px',
                    transition: 'left 0.2s'
                  }} />
                </button>
              </div>

              {settings.speechEnabled && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ color: '#8b949e', fontSize: '14px' }}>Speed:</span>
                  <input
                    type="range"
                    min="0.5"
                    max="1.5"
                    step="0.1"
                    value={settings.speechSpeed}
                    onChange={(e) => setSettings(s => ({ ...s, speechSpeed: parseFloat(e.target.value) }))}
                    style={{ flex: 1, cursor: 'pointer' }}
                  />
                  <span style={{ color: '#c9d1d9', minWidth: '40px' }}>{settings.speechSpeed.toFixed(1)}x</span>
                </div>
              )}
            </div>

            {/* Theme Settings */}
            <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid #555d66', borderRadius: '8px' }}>
              <h3 style={{ color: '#58a6ff', fontSize: '16px', marginBottom: '12px', marginTop: 0 }}>🎨 Theme</h3>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.entries(themes).map(([key, theme]) => (
                  <button
                    key={key}
                    onClick={() => setSettings(s => ({ ...s, theme: key }))}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: settings.theme === key ? '2px solid #58a6ff' : '2px solid #30363d',
                      backgroundColor: theme.background,
                      color: theme.textColor,
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: settings.theme === key ? '600' : '400'
                    }}
                  >
                    {theme.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Font Size Settings */}
            <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid #555d66', borderRadius: '8px' }}>
              <h3 style={{ color: '#58a6ff', fontSize: '16px', marginBottom: '12px', marginTop: 0 }}>📝 Text Size</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[14, 16, 18, 20].map((size) => (
                  <button
                    key={size}
                    onClick={() => setSettings(s => ({ ...s, fontSize: size }))}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: settings.fontSize === size ? '2px solid #58a6ff' : '2px solid #30363d',
                      backgroundColor: '#21262d',
                      color: settings.fontSize === size ? '#58a6ff' : '#c9d1d9',
                      cursor: 'pointer',
                      fontSize: `${size}px`,
                      fontWeight: settings.fontSize === size ? '600' : '400'
                    }}
                  >
                    {size === 14 ? 'S' : size === 16 ? 'M' : size === 18 ? 'L' : 'XL'}
                  </button>
                ))}
              </div>
            </div>

            {/* Tutor Mode Settings */}
            <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid #555d66', borderRadius: '8px' }}>
              <h3 style={{ color: '#58a6ff', fontSize: '16px', marginBottom: '12px', marginTop: 0 }}>🎓 Programming Exercise Tutor Mode</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {tutorConfig.enabledTutorModes.stepByStep && (
                <button
                  onClick={() => changeTutorMode('stepByStep')}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: (settings.tutorMode || 'stepByStep') === 'stepByStep' ? '2px solid #1a5c2a' : '2px solid #30363d',
                    backgroundColor: (settings.tutorMode || 'stepByStep') === 'stepByStep' ? '#1a5c2a' : '#21262d',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: (settings.tutorMode || 'stepByStep') === 'stepByStep' ? '600' : '400',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>🔍 Step-by-Step Discovery ⭐</div>
                  <div style={{ fontSize: '12px', color: (settings.tutorMode || 'stepByStep') === 'stepByStep' ? '#a8d8a8' : '#8b949e' }}>
                    You figure out each step, then code it (recommended - best for learning)
                  </div>
                </button>
                )}
                {tutorConfig.enabledTutorModes.allSteps && (
                <button
                  onClick={() => changeTutorMode('allSteps')}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: settings.tutorMode === 'allSteps' ? '2px solid #1a4d7a' : '2px solid #30363d',
                    backgroundColor: settings.tutorMode === 'allSteps' ? '#1a4d7a' : '#21262d',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: settings.tutorMode === 'allSteps' ? '600' : '400',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>📋 Show All Steps</div>
                  <div style={{ fontSize: '12px', color: settings.tutorMode === 'allSteps' ? '#a8c8e8' : '#8b949e' }}>
                    Tutor shows all steps upfront, then guides you through each
                  </div>
                </button>
                )}
                {tutorConfig.enabledTutorModes.adaptive && (
                <button
                  onClick={() => changeTutorMode('adaptive')}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: settings.tutorMode === 'adaptive' ? '2px solid #8b6914' : '2px solid #30363d',
                    backgroundColor: settings.tutorMode === 'adaptive' ? '#8b6914' : '#21262d',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: settings.tutorMode === 'adaptive' ? '600' : '400',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>🔄 Adaptive</div>
                  <div style={{ fontSize: '12px', color: settings.tutorMode === 'adaptive' ? '#d4c896' : '#8b949e' }}>
                    Starts step-by-step, shows all steps if you struggle
                  </div>
                </button>
                )}
              </div>
              <p style={{ color: '#f0ad4e', fontSize: '12px', marginTop: '12px', marginBottom: 0, fontStyle: 'italic' }}>
                💡 Changes take effect on your next exercise
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => setShowSettings(false)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a5c2a',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseContentView;
